/**
 * "Copy link" (23 Oct): the two links a ticket can be shared by, and what goes
 * on the clipboard.
 *
 * - The **HelpDesk link** (`<app origin>/t/<number>`) is for the requester:
 *   the short route Ivanti's emails already use, resolved to the ticket page.
 * - The **ITSM link** opens the record in the Ivanti console, for IT. Same
 *   pattern as Ticket search's console link (`ticket_search.console_url` in
 *   the backend); the tenant's origin comes from `/auth/me` → `itsm_url`,
 *   which only IT and admins get.
 *
 * The clipboard gets HTML (`<a href>Incident 1032 – Ethernet …</a>`) and the
 * bare URL as plain text, so Teams and Outlook paste a tidy, clickable title
 * and anything plain-text pastes a working link. Import-free so
 * `tests/copy-link.test.mjs` can load it directly.
 */

export type LinkTarget = "helpdesk" | "itsm";

export interface LinkOption {
  target: LinkTarget;
  url: string;
}

/** Longest summary after the type and number; Teams wraps anything longer. */
export const LINK_SUMMARY_MAX = 60;

function digitsOf(number: string | null | undefined): string | null {
  const digits = (number ?? "").trim().replace(/^#+/, "").trim();
  return /^\d+$/.test(digits) ? String(Number(digits)) : null;
}

/** The ticket in the Ivanti console, by number; null without a tenant or a numeric number. */
export function itsmTicketUrl(
  base: string | null | undefined,
  kind: string,
  number: string | null | undefined,
): string | null {
  const origin = (base ?? "").trim().replace(/\/+$/, "");
  const digits = digitsOf(number);
  if (origin === "" || digits === null) {
    return null;
  }
  const [obj, field] =
    kind === "SERVICE_REQUEST"
      ? ["ServiceReq", "ServiceReqNumber"]
      : ["Incident", "IncidentNumber"];
  return (
    `${origin}/Login.aspx?Scope=ObjectWorkspace&CommandId=Search` +
    `&ObjectType=${obj}%23&CommandData=${field},%3D,0,${digits},int,AND|`
  );
}

/** `<origin>/t/<number>`: the requester's way in, the app's own short route. */
export function helpdeskTicketUrl(
  origin: string | null | undefined,
  number: string | null | undefined,
): string | null {
  const host = (origin ?? "").trim().replace(/\/+$/, "");
  const cleaned = (number ?? "").trim().replace(/^#+/, "").trim();
  if (host === "" || cleaned === "") {
    return null;
  }
  return `${host}/t/${encodeURIComponent(cleaned)}`;
}

/**
 * "Incident 1032 – Ethernet network card missing…": the type and number
 * (already translated, e.g. "Incident 1032"), then the summary on one line,
 * cut at a word near `max` characters with an ellipsis.
 */
export function linkTitle(
  reference: string,
  summary: string | null | undefined,
  max: number = LINK_SUMMARY_MAX,
): string {
  const text = (summary ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (text === "") {
    return reference;
  }
  if (text.length <= max) {
    return `${reference} – ${text}`;
  }
  const cut = text.slice(0, max);
  const space = cut.lastIndexOf(" ");
  const short = (space >= max * 0.6 ? cut.slice(0, space) : cut).replace(/[\s,.;:–-]+$/, "");
  return `${reference} – ${short}…`;
}

/**
 * The links offered for a ticket: the HelpDesk link for everyone, the ITSM
 * link too for IT and admins (`itsmBase` is set for them only). The ITSM
 * link comes first for IT — it is the one they share with colleagues.
 * `itsmUrl` passes a link the server already built (Ticket search rows).
 */
export function copyLinkOptions(input: {
  kind: string;
  number: string | null | undefined;
  origin: string | null | undefined;
  itsmBase?: string | null;
  itsmUrl?: string | null;
  helpdesk?: boolean;
}): LinkOption[] {
  const options: LinkOption[] = [];
  const itsm = input.itsmUrl ?? itsmTicketUrl(input.itsmBase, input.kind, input.number);
  if (itsm !== null && itsm !== "") {
    options.push({ target: "itsm", url: itsm });
  }
  const helpdesk = input.helpdesk === false ? null : helpdeskTicketUrl(input.origin, input.number);
  if (helpdesk !== null) {
    options.push({ target: "helpdesk", url: helpdesk });
  }
  return options;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** What goes on the clipboard: a link with the title, and the bare URL. */
export function linkClipboardPayload(title: string, url: string): { html: string; text: string } {
  return {
    html: `<a href="${escapeHtml(url)}">${escapeHtml(title)}</a>`,
    text: url,
  };
}

/** The bits of `navigator.clipboard` used, so tests can pass a stand-in. */
export interface ClipboardLike {
  write?: (items: unknown[]) => Promise<void>;
  writeText?: (text: string) => Promise<void>;
}

/**
 * Copies the link as rich text and plain text where the browser can
 * (`clipboard.write` with a `ClipboardItem`), else the URL alone. Resolves to
 * "rich" or "plain"; rejects when nothing could be copied.
 */
export async function copyLink(
  title: string,
  url: string,
  clipboard: ClipboardLike | undefined = globalThis.navigator?.clipboard as
    | ClipboardLike
    | undefined,
  Item: (new (items: Record<string, Blob>) => unknown) | undefined = (
    globalThis as { ClipboardItem?: new (items: Record<string, Blob>) => unknown }
  ).ClipboardItem,
): Promise<"rich" | "plain"> {
  const payload = linkClipboardPayload(title, url);
  if (clipboard?.write !== undefined && Item !== undefined) {
    try {
      await clipboard.write([
        new Item({
          "text/html": new Blob([payload.html], { type: "text/html" }),
          "text/plain": new Blob([payload.text], { type: "text/plain" }),
        }),
      ]);
      return "rich";
    } catch {
      // Firefox before 127 and some embedded browsers refuse HTML: plain below.
    }
  }
  if (clipboard?.writeText === undefined) {
    throw new Error("Clipboard unavailable");
  }
  await clipboard.writeText(payload.text);
  return "plain";
}
